import React, { useState } from 'react';
import {
  Typography,
  Stack,
  Button,
  TextField,
  Container,
  Link,
  IconButton,
  InputAdornment,
} from '@mui/material';
import HeroBannerImage from '../assets/images/banner3.jpg';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref, push, set, get } from 'firebase/database';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [age, setAge] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [aimWeight, setAimWeight] = useState('');
  const [error, setError] = useState('');
  const [registered, setRegistered] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Perform field validations
    if (name.trim() === '') {
      setError('Name is required');
      return;
    }

    if (email.trim() === '') {
      setError('Email is required');
      return;
    }

    if (password.trim() === '') {
      setError('Password is required');
      return;
    }

    if (confirmPassword.trim() === '') {
      setError('Confirm Password is required');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (age.trim() === '' || parseInt(age) < 0) {
      setError('Age must be a non-negative number');
      return;
    }

    if (height.trim() === '' || parseInt(height) < 0) {
      setError('Height must be a non-negative number');
      return;
    }

    if (weight.trim() === '' || parseInt(weight) < 0) {
      setError('Weight must be a non-negative number');
      return;
    }

    if (aimWeight.trim() === '' || parseInt(aimWeight) < 0) {
      setError('Aim Weight must be a non-negative number');
      return;
    }

    // Check if email already exists in the database
    const db = getDatabase();
    const usersRef = ref(db, 'users');
    const snapshot = await get(usersRef);

    if (snapshot.exists()) {
      const users = Object.values(snapshot.val());
      const existingUser = users.find((user) => user.email === email);
      if (existingUser) {
        setError('Email already exists. Please choose a different email.');
        return;
      }
    }

    // If email doesn't exist, proceed with registration
    const newUser = {
      name: name,
      email: email,
      age: age,
      height: height,
      weight: weight,
      aimWeight: aimWeight,
    };

    const auth = getAuth();
    createUserWithEmailAndPassword(auth, email, password)
      .then(async (userCredential) => {
        // Signed in successfully
        const user = userCredential.user;
        // Store the user in the database
        try {
          const newRef = push(usersRef);
          await set(newRef, newUser);
          setRegistered(true);
          navigate('/login');
        } catch (error) {
          console.log(error);
          setError('An error occurred during registration. Please try again.');
        }
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        setError(errorMessage);
      });
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prevShowConfirmPassword) => !prevShowConfirmPassword);
  };

  return (
    <Container maxWidth="md">
      <Stack gap={5} sx={{ p: '20px' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" gap={3}>
          <img
            src={HeroBannerImage}
            alt="Exercise Detail"
            loading="lazy"
            style={{ maxWidth: '65%', height: 'auto' , borderRadius:'20px',marginRight:'20px', marginLeft:'-20px' }}
          />

          <Stack sx={{ gap: { xs: '20px', md: '35px' } }} maxWidth="100%">
            <Typography variant="h3" textAlign="center" mt={10}>
              Register
            </Typography>

            <form onSubmit={handleSubmit}>
              <Stack direction="column" gap={3}>
                <TextField
                  label="Name"
                  variant="outlined"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={error && error.includes('Name')}
                  helperText={error && error.includes('Name') && error}
                  required
                />

                <TextField
                  type="email"
                  label="Email"
                  variant="outlined"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={error && error.includes('Email')}
                  helperText={error && error.includes('Email') && error}
                  required
                />

                <TextField
                  type={showPassword ? 'text' : 'password'}
                  label="Password"
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={error && error.includes('Password')}
                  helperText={error && error.includes('Password') && error}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={togglePasswordVisibility} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  required
                />

                <TextField
                  type={showConfirmPassword ? 'text' : 'password'}
                  label="Confirm Password"
                  variant="outlined"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={error && error.includes('Confirm Password')}
                  helperText={error && error.includes('Confirm Password') && error}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={toggleConfirmPasswordVisibility} edge="end">
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  required
                />

                <TextField
                  type="number"
                  label="Age"
                  variant="outlined"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  error={error && error.includes('Age')}
                  helperText={error && error.includes('Age') && error}
                  required
                />

                <TextField
                  type="number"
                  label="Height (cm)"
                  variant="outlined"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  error={error && error.includes('Height')}
                  helperText={error && error.includes('Height') && error}
                  required
                />

                <TextField
                  type="number"
                  label="Weight (kg)"
                  variant="outlined"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  error={error && error.includes('Weight')}
                  helperText={error && error.includes('Weight') && error}
                  required
                />

                <TextField
                  type="number"
                  label="Aim Weight (kg)"
                  variant="outlined"
                  value={aimWeight}
                  onChange={(e) => setAimWeight(e.target.value)}
                  error={error && error.includes('Aim Weight')}
                  helperText={error && error.includes('Aim Weight') && error}
                  required
                />

                <Button type="submit" variant="contained" fullWidth>
                  Register
                </Button>
              </Stack>
            </form>

            <Typography variant="body2" textAlign="center">
              Already have an account?{' '}
              <Link href="/login" color="primary">
                Login
              </Link>
            </Typography>

            {registered && (
              <Typography variant="body2" textAlign="center" color="primary">
                Registration successful! Please proceed to{' '}
                <Link href="/login" color="inherit">
                  Login
                </Link>
              </Typography>
            )}

            {error && (
              <Typography variant="body2" textAlign="center" color="error">
                {error}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
};

export default Register;
